import {
  FormEventHandler,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Flex, Text, TextField, Button, Spinner, Link } from "@radix-ui/themes";
import * as Form from "@radix-ui/react-form";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { Tooltip } from "@/components/shared/tooltip";
import {
  ConnectIntegrationFormPayload,
  IntegrationName,
  IntegrationType,
  isIntegrationCompleted,
} from "../../../../api/integration/Integration.model";
import { StepHeader, StepMain } from "../parts";
import { integrationNameToGalleryItem } from "../../../../api/integration/integrationNameToGalleryItem";
import styles from "../AddIntegration.module.scss";
import { useAuth0 } from "@auth0/auth0-react";
import { useIntegrations } from "../../../../api/integration/Integration.api";
import Checkbox from "../../../../components/shared/inputs/checkbox";
import { useIntegrationsTracking } from "../../useIntegrationsTracking";

interface FieldControlProps {
  label: string;
  name: string;
  type?: TextField.RootProps["type"];
  required?: boolean;
  placeholder?: string;
  error?: string;
}

function FieldControl({
  label,
  name,
  type = "text",
  required,
  placeholder = label,
  error,
  children,
}: PropsWithChildren<FieldControlProps>) {
  return (
    <Form.Field name={name} className={styles.FieldControl}>
      <Form.Label>
        <Text size={"1"} weight={"medium"}>
          {label}
        </Text>
      </Form.Label>

      <Form.Control asChild>
        <TextField.Root
          type={type}
          size="2"
          placeholder={placeholder}
          required={required}
        />
      </Form.Control>

      <Form.Message className={styles.Message} match="valueMissing">
        <Text size={"1"} weight={"medium"} color={"red"}>
          {`Please enter a ${name}.`}
        </Text>
      </Form.Message>

      {error != null && (
        <Form.Message className={styles.Message}>
          <Text size={"1"} weight={"medium"} color={"red"}>
            {error}
          </Text>
        </Form.Message>
      )}

      {children}
    </Form.Field>
  );
}

const docsLinks: Record<IntegrationName, string | undefined> = {
  zendesk:
    "https://support.zendesk.com/hc/en-us/articles/4408889192858-Managing-access-to-the-Zendesk-API#topic_tcb_fk1_2yb",
  document360: "https://apidocs.document360.com/apidocs/api-token",
  gitbook: "https://developer.gitbook.com/gitbook-api/authentication",
  front: "https://app.frontapp.com/settings/home",
  freshdesk: undefined,
  salesforce: undefined,
  salesforce_livechat: undefined,
  hubspot: undefined,
  intercom:
    "https://developers.intercom.com/docs/build-an-integration/learn-more/authentication",
  kustomer: undefined,
  // gladly: undefined,
  slack: undefined,
  jira: undefined,
  notion: undefined,
  confluence: undefined,
  google_drive: undefined,
  monday: "https://developer.monday.com/api-reference/docs/authentication",
  helpscout: undefined,
  plain: undefined,
  gorgias: undefined,
  apibased: undefined,
  priority: undefined,
  smooch:
    "https://integrations.quack.ai/BKUZmzGN2Mmipf4UROTF/chat/connecting-quack-ai-to-zendesk-conversations-api",
  elevio: undefined,
};

type ServerErrors = {
  token?: string;
  general?: string;
};

type NewZendeskFormPayload = {
  email: string;
  subdomain: string;
  token: string;
};

function NewZendeskConnectForm({ onSubmit }: ConnectFormProps) {
  const { trackIntegrationViewed } = useIntegrationsTracking();
  const [loading, setLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState<ServerErrors>();

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    async (event) => {
      // prevent default form submission
      event.preventDefault();

      try {
        setLoading(true);
        const data = Object.fromEntries(new FormData(event.currentTarget));
        const { email, subdomain, token } = data as NewZendeskFormPayload;

        await onSubmit({
          subdomain,
          email,
          token,
          tokenType: "API",
        });
      } catch (error) {
        setServerErrors({
          general: "An unknown error occurred. Please contact support.",
        });
      } finally {
        setLoading(false);
      }
    },
    [onSubmit]
  );

  const handleClearServerErrors = useCallback(() => {
    setServerErrors(undefined);
  }, []);

  return (
    <Form.Root
      onSubmit={handleSubmit}
      onClearServerErrors={handleClearServerErrors}
      className={styles.Form}
    >
      <div className={styles.FieldList}>
        <FieldControl
          label={"Zendesk Admin's Email"}
          name={"email"}
          type={"email"}
          required
        />

        <FieldControl
          label={"Zendesk subdomain"}
          name={"subdomain"}
          type={"text"}
          required
        />

        <FieldControl
          label={"Zendesk token"}
          name={"token"}
          type={"text"}
          required
        >
          {docsLinks["zendesk"] != null && (
            <Link
              href={docsLinks["zendesk"]}
              target="_blank"
              color="gray"
              highContrast
              size={"2"}
              onClick={() => {
                trackIntegrationViewed("HELPDESK", "zendesk");
              }}
            >
              I can't find my token
            </Link>
          )}
        </FieldControl>

        {serverErrors?.general != null && (
          <Text size={"1"} weight={"medium"} color={"red"}>
            {serverErrors.general}
          </Text>
        )}
      </div>

      <Form.Submit asChild>
        <div className={styles.Footer}>
          <Button color="gray" variant="solid" highContrast type="submit">
            <Spinner loading={loading} />
            Continue
          </Button>
        </div>
      </Form.Submit>
    </Form.Root>
  );
}

type FrontFormPayload = {
  email: string;
  subdomain: string;
  token: string;
};

function FrontConnectForm({ onSubmit }: ConnectFormProps) {
  const { trackIntegrationViewed } = useIntegrationsTracking();
  const [loading, setLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState<ServerErrors>();

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    async (event) => {
      // prevent default form submission
      event.preventDefault();

      try {
        setLoading(true);
        const data = Object.fromEntries(new FormData(event.currentTarget));
        const { email, subdomain, token } = data as FrontFormPayload;

        await onSubmit({
          subdomain,
          email,
          token,
          tokenType: "API",
        });
      } catch (error) {
        setServerErrors({
          general: "An unknown error occurred. Please contact support.",
        });
      } finally {
        setLoading(false);
      }
    },
    [onSubmit]
  );

  const handleClearServerErrors = useCallback(() => {
    setServerErrors(undefined);
  }, []);

  return (
    <Form.Root
      onSubmit={handleSubmit}
      onClearServerErrors={handleClearServerErrors}
      className={styles.Form}
    >
      <div className={styles.FieldList}>
        <FieldControl
          label={"Front Admin's Email"}
          name={"email"}
          type={"email"}
          required
        />

        <FieldControl
          label={"Front subdomain"}
          name={"subdomain"}
          type={"text"}
          required
        />

        <FieldControl
          label={"Front token"}
          name={"token"}
          type={"text"}
          required
        >
          {docsLinks["front"] != null && (
            <Link
              href={docsLinks["front"]}
              target="_blank"
              color="gray"
              highContrast
              size={"2"}
              onClick={() => {
                trackIntegrationViewed("HELPDESK", "front");
              }}
            >
              I can't find my token
            </Link>
          )}
        </FieldControl>

        {serverErrors?.general != null && (
          <Text size={"1"} weight={"medium"} color={"red"}>
            {serverErrors.general}
          </Text>
        )}
      </div>

      <Form.Submit asChild>
        <div className={styles.Footer}>
          <Button color="gray" variant="solid" highContrast type="submit">
            <Spinner loading={loading} />
            Continue
          </Button>
        </div>
      </Form.Submit>
    </Form.Root>
  );
}

type IntercomFormPayload = {
  email: string;
  subdomain: string;
  token: string;
};

function IntercomConnectForm({ onSubmit }: ConnectFormProps) {
  const { trackIntegrationViewed } = useIntegrationsTracking();
  const [loading, setLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState<ServerErrors>();

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    async (event) => {
      // prevent default form submission
      event.preventDefault();

      try {
        setLoading(true);
        const data = Object.fromEntries(new FormData(event.currentTarget));
        const { email, subdomain, token } = data as IntercomFormPayload;

        await onSubmit({
          subdomain,
          email,
          token,
          tokenType: "API",
        });
      } catch (error) {
        setServerErrors({
          general: "An unknown error occurred. Please contact support.",
        });
      } finally {
        setLoading(false);
      }
    },
    [onSubmit]
  );

  const handleClearServerErrors = useCallback(() => {
    setServerErrors(undefined);
  }, []);

  return (
    <Form.Root
      onSubmit={handleSubmit}
      onClearServerErrors={handleClearServerErrors}
      className={styles.Form}
    >
      <div className={styles.FieldList}>
        <FieldControl
          label={"Intercom Admin's Email"}
          name={"email"}
          type={"email"}
          required
        />

        <FieldControl
          label={"Intercom subdomain"}
          name={"subdomain"}
          type={"text"}
          required
        />

        <FieldControl
          label={"Intercom token"}
          name={"token"}
          type={"text"}
          required
        >
          {docsLinks["intercom"] != null && (
            <Link
              href={docsLinks["intercom"]}
              target="_blank"
              color="gray"
              highContrast
              size={"2"}
              onClick={() => {
                trackIntegrationViewed("HELPDESK", "intercom");
              }}
            >
              I can't find my token
            </Link>
          )}
        </FieldControl>

        {serverErrors?.general != null && (
          <Text size={"1"} weight={"medium"} color={"red"}>
            {serverErrors.general}
          </Text>
        )}
      </div>

      <Form.Submit asChild>
        <div className={styles.Footer}>
          <Button color="gray" variant="solid" highContrast type="submit">
            <Spinner loading={loading} />
            Continue
          </Button>
        </div>
      </Form.Submit>
    </Form.Root>
  );
}

type ExistingZendeskFormPayload = {
  userConfimation: boolean;
};

function ExistingZendeskConnectForm({ onSubmit }: ConnectFormProps) {
  const [payload, setPayload] = useState<ExistingZendeskFormPayload>({
    userConfimation: true,
  });

  const [loading, setLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState<ServerErrors>();

  const handleSubmit = useCallback(async () => {
    try {
      setServerErrors(undefined);
      setLoading(true);

      await onSubmit({
        email: "email",
        token: "token",
        tokenType: "API",
      });
    } catch (error) {
      setServerErrors({
        general: "An unknown error occurred. Please contact support.",
      });
    } finally {
      setLoading(false);
    }
  }, [onSubmit]);

  return (
    <Flex height={"100%"} direction={"column"}>
      <Flex px={"45px"}>
        <Text color="gray" size={"2"}>
          Your Zendesk account is already connected. We will reuse the same
          token to connect your knowledge source.
        </Text>
      </Flex>

      <div className={styles.Form}>
        <div className={styles.FieldList}>
          <div className={styles.FieldControl}>
            <Flex direction={"column"}>
              <Flex justify={"between"} align={"center"}>
                <Checkbox
                  label={
                    "I confirm to reuse the existing Zendesk token for this integration."
                  }
                  value={payload.userConfimation}
                  onChange={(checked) =>
                    setPayload({ userConfimation: checked })
                  }
                />
              </Flex>
            </Flex>
          </div>

          {serverErrors?.general != null && (
            <Text size={"1"} weight={"medium"} color={"red"}>
              {serverErrors.general}
            </Text>
          )}
        </div>

        <div className={styles.Footer}>
          <Button
            color="gray"
            variant="solid"
            highContrast
            onClick={handleSubmit}
            disabled={payload.userConfimation !== true}
          >
            <Spinner loading={loading} />
            Continue
          </Button>
        </div>
      </div>
    </Flex>
  );
}

interface ConnectFormProps {
  onSubmit: (value: ConnectIntegrationFormPayload) => Promise<void>;
}

function ZendeskConnectForm(props: ConnectFormProps) {
  const { data, isLoading } = useIntegrations();

  const existingZendeskIntegration = useMemo(
    () =>
      data?.find(
        (integration) =>
          integration.name === "zendesk" && isIntegrationCompleted(integration)
      ),
    [data]
  );

  if (isLoading) {
    return (
      <Flex pt={"100px"} width={"100%"} justify={"center"}>
        <Spinner size={"3"} />
      </Flex>
    );
  }

  if (existingZendeskIntegration != null) {
    return <ExistingZendeskConnectForm {...props} />;
  }

  return <NewZendeskConnectForm {...props} />;
}

type SmoochFormPayload = {
  appId: string;
  keyId: string;
  secretKey: string;
};

function SmoochConnectForm({ onSubmit }: ConnectFormProps) {
  const [loading, setLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState<ServerErrors>();

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    async (event) => {
      event.preventDefault();

      try {
        setLoading(true);
        const data = Object.fromEntries(new FormData(event.currentTarget));
        const { appId, keyId, secretKey } = data as SmoochFormPayload;

        await onSubmit({
          appId: appId,
          keyId: keyId,
          secretKey: secretKey,
          tokenType: "API",
        });
      } catch (error) {
        setServerErrors({
          general: "An unknown error occurred. Please contact support.",
        });
      } finally {
        setLoading(false);
      }
    },
    [onSubmit]
  );

  const handleClearServerErrors = useCallback(() => {
    setServerErrors(undefined);
  }, []);

  return (
    <Form.Root
      onSubmit={handleSubmit}
      onClearServerErrors={handleClearServerErrors}
      className={styles.Form}
    >
      <div className={styles.FieldList}>
        <Flex gap="2" align="center" mb="2">
          <Text size="2" color="gray">
            How to get these credentials?
          </Text>
          <Tooltip>
            <Tooltip.Trigger>
              <InfoCircledIcon style={{ cursor: "pointer" }} />
            </Tooltip.Trigger>
            <Tooltip.Content>
              <Flex direction="column" gap="1" style={{ maxWidth: 300 }}>
                <Text size="1" weight="bold">
                  Create an API token
                </Text>
                <Text size="1">
                  1. Login to your Zendesk and go to the Admin page.
                </Text>
                <Text size="1">
                  2. Scroll down to the “Conversations API” tab under “APIs”.
                </Text>
                <Text size="1">3. Click on “Create API key”.</Text>
                <Text size="1">
                  4. Name the API key “Quack AI” and copy the “App ID”, “Key ID”
                  and “Secret Key”.
                </Text>
              </Flex>
            </Tooltip.Content>
          </Tooltip>
        </Flex>

        <FieldControl label={"App ID"} name={"appId"} type={"text"} required />

        <FieldControl label={"Key ID"} name={"keyId"} type={"text"} required />

        <FieldControl
          label={"Secret Key"}
          name={"secretKey"}
          type={"password"}
          required
        />

        {serverErrors?.general != null && (
          <Text size={"1"} weight={"medium"} color={"red"}>
            {serverErrors.general}
          </Text>
        )}
      </div>

      <Form.Submit asChild>
        <div className={styles.Footer}>
          <Button color="gray" variant="solid" highContrast type="submit">
            <Spinner loading={loading} />
            Continue
          </Button>
          <Button
            color="gray"
            variant="soft"
            highContrast
            type="button"
            onClick={() => window.open(docsLinks["smooch"], "_blank")}
          >
            See Full Documentation
          </Button>
        </div>
      </Form.Submit>
    </Form.Root>
  );
}

type KnowleBaseFormPayload = {
  token: string;
};

function KnowleBaseConnectForm({
  name,
  onSubmit,
}: { name: IntegrationName } & ConnectFormProps) {
  const { trackIntegrationViewed } = useIntegrationsTracking();
  const { user } = useAuth0();

  const [serverErrors, setServerErrors] = useState<ServerErrors>();
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    async (event) => {
      // prevent default form submission
      event.preventDefault();

      try {
        setLoading(true);
        const data = Object.fromEntries(new FormData(event.currentTarget));
        const { token } = data as KnowleBaseFormPayload;

        await onSubmit({
          email: user?.email ?? "email",
          token,
          tokenType: "API",
        });
      } catch (error) {
        setServerErrors({
          general: "An unknown error occurred. Please contact support.",
        });
      } finally {
        setLoading(false);
      }
    },
    [user, onSubmit]
  );

  const handleClearServerErrors = useCallback(() => {
    setServerErrors(undefined);
  }, []);

  const { title } = integrationNameToGalleryItem[name];

  return (
    <Form.Root
      onSubmit={handleSubmit}
      onClearServerErrors={handleClearServerErrors}
      className={styles.Form}
    >
      <div className={styles.FieldList}>
        <FieldControl
          label={`${title} token`}
          name={"token"}
          type={"text"}
          required
        >
          {docsLinks[name] != null && (
            <Link
              href={docsLinks[name]}
              target="_blank"
              color="gray"
              highContrast
              size={"2"}
              onClick={() => {
                trackIntegrationViewed("KNOWLEDGE_BASE", name);
              }}
            >
              I can't find my token
            </Link>
          )}
        </FieldControl>

        {serverErrors?.general != null && (
          <Text size={"1"} weight={"medium"} color={"red"}>
            {serverErrors.general}
          </Text>
        )}
      </div>

      <Form.Submit asChild>
        <div className={styles.Footer}>
          <Button color="gray" variant="solid" highContrast type="submit">
            <Spinner loading={loading} />
            Continue
          </Button>
        </div>
      </Form.Submit>
    </Form.Root>
  );
}

interface ConnectIntegrationProps {
  type: IntegrationType;
  name: IntegrationName;
  onSubmit: (value: ConnectIntegrationFormPayload) => Promise<void>;
}

function ConnectIntegration({ type, name, onSubmit }: ConnectIntegrationProps) {
  const title = `Connect to ${integrationNameToGalleryItem[name].title}`;

  return (
    <StepMain>
      <Flex pt={"35px"} px={"45px"}>
        <StepHeader title={title} />
      </Flex>

      <Flex flexGrow={"1"} overflow={"hidden"} pt={"16px"}>
        {name === "zendesk" && <ZendeskConnectForm onSubmit={onSubmit} />}
        {name === "front" && <FrontConnectForm onSubmit={onSubmit} />}
        {name === "intercom" && <IntercomConnectForm onSubmit={onSubmit} />}
        {name === "smooch" && <SmoochConnectForm onSubmit={onSubmit} />}

        {(type === "KNOWLEDGE_BASE" || type === "WIKI") &&
          name !== "zendesk" && (
            <KnowleBaseConnectForm name={name} onSubmit={onSubmit} />
          )}
      </Flex>
    </StepMain>
  );
}

export default ConnectIntegration;
